/**
 * Ended-session surface: every result from this round, the count of earlier rounds in this room, and the
 * host's "return to lobby" control (docs/ARCHITECTURE.md §3).
 */
import { DECISION_MAKER_ID, HOT_POTATO_ID, MOMENTUM_BOARD_V2_ID, DecisionMakerResultPayload } from '@momentum/modules/manifests';
import type { ModuleResultPublic } from '@momentum/shared';
import { useSessionContext } from './SessionContext.ts';
import { strings } from '../shell/strings.ts';
import { z } from 'zod';
import './results.css';
import { ResetForNewGroup } from './ResetForNewGroup.tsx';

export function Results() {
  const { view, self, client } = useSessionContext();
  const session = view.session;
  const results = session.results?.results ?? [];

  const board = results.find(result => result.moduleId === MOMENTUM_BOARD_V2_ID);
  const payload = z.object({ crystalsCollected: z.number() }).safeParse(board?.payload);
  const crystals = payload.success ? payload.data.crystalsCollected : null;
  const completedActivities = results.filter(result => result.moduleId !== MOMENTUM_BOARD_V2_ID && result.status === 'completed');
  const participants = new Set(results.flatMap(result => result.participantIds));
  return (
    <div className="stack session-results">
      <header><span className="session-results__eyebrow">Momentum · Session summary</span>
      <h1>{crystals === 3 ? 'Three crystals. One team.' : 'Your session, together.'}</h1>
      <p className="session-results__intro">{crystals === 3 ? 'You reached the shared goal and finished the session. Here’s what your team completed.' : 'The session has ended. Here’s what your team completed.'}</p></header>
      <div className="session-results__totals">
        <div><strong>{crystals === null ? '—' : `${crystals} / 3`}</strong><span>Team crystals</span></div>
        <div><strong>{completedActivities.length}</strong><span>Activities completed</span></div>
        <div><strong>{participants.size}</strong><span>Teammates</span></div>
      </div>
      {session.history.length > 0 ? <p className="text-muted">{strings.results.historyCount(session.history.length)}</p> : null}

      <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {results.filter(result => result.moduleId !== MOMENTUM_BOARD_V2_ID).map((result) => (
          <ResultCard key={result.instanceId} result={result} />
        ))}
      </ul>

      <div className="row">
        {self && self.isHost ? (
          <button
            type="button"
            className="button button-primary"
            onClick={() => client.send('session.return_to_lobby', {}, 'session')}
          >
            Play again with the same group
          </button>
        ) : null}
        {self && self.isHost ? <ResetForNewGroup /> : null}
        <button type="button" className="button" onClick={() => client.send('session.leave', {}, 'session')}>
          {strings.results.leave}
        </button>
      </div>
    </div>
  );
}

function ResultCard({ result }: { result: ModuleResultPublic }) {
  if (result.moduleId === DECISION_MAKER_ID) {
    const parsed = DecisionMakerResultPayload.safeParse(result.payload);
    return <li className="session-results__activity stack-sm">
      <h2>{parsed.success ? parsed.data.ending.title : 'Decision Maker ended'}</h2>
      <p>{result.summary}</p>
      {parsed.success ? <>
        <blockquote>{parsed.data.ending.clientLine}</blockquote>
        <p>{parsed.data.ending.summary}</p>
        <p><strong>Where service stands:</strong> {parsed.data.ending.serviceStatus}</p>
        <p><strong>The next commitment:</strong> {parsed.data.ending.followUp}</p>
        <details><summary>Review the team’s decisions</summary><ol>{parsed.data.history.map(entry =>
          <li key={entry.nodeId}><strong>{entry.label}</strong><p>{entry.consequence}</p></li>
        )}</ol></details>
      </> : null}
    </li>;
  }
  return <li className="session-results__activity">
    <h2>{result.moduleId === HOT_POTATO_ID ? 'Hot Potato' : 'Team activity'}</h2>
    <p>{result.summary ?? (result.status === 'completed' ? 'Completed together.' : 'Activity ended before completion.')}</p>
    <span className="text-muted">{result.status === 'completed' ? 'Completed' : 'Not completed'}</span>
  </li>;
}
